/**
 * A user's own saved prompts for one place in the app (a Program tab or Media
 * AI). Clicking a saved prompt pulls its text into that page's input, where it
 * can be edited for this one request without changing the saved copy.
 *
 * Exports:
 *   useCustomPrompts(scope)  — loads and edits the user's prompts for a scope
 *   PromptPicker             — searchable dropdown to choose one (lists can be long)
 *   PromptManager            — "Manage prompts" modal: add, edit, delete, set the default
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiStar, FiEdit2, FiTrash2, FiPlus, FiX, FiSearch } from 'react-icons/fi';
import { customPromptService } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { SearchableSelect } from './SearchableSelect';

const FALLBACK_LIMITS = { perScope: 20, labelMax: 100, textMax: 2000 };

export const useCustomPrompts = (scope) => {
  const [prompts, setPrompts] = useState([]);
  const [limits, setLimits] = useState(FALLBACK_LIMITS);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    if (!scope) { setPrompts([]); setLoaded(true); return; } // page without saved prompts
    try {
      const data = await customPromptService.list(scope);
      setPrompts(data.prompts || []);
      if (data.limits) setLimits(data.limits);
    } catch {
      setPrompts([]);
    } finally {
      setLoaded(true);
    }
  }, [scope]);

  useEffect(() => {
    setLoaded(false);
    reload();
  }, [reload]);

  return { prompts, limits, loaded, reload };
};

// ─── Picker ──────────────────────────────────────────────────────────────────

/**
 * Choose a saved prompt — type to search. allowNone adds "No prompt" (value
 * ''). Compact by default, to sit in a composer's option row.
 */
export const PromptPicker = ({ id, prompts, value, onChange, disabled, allowNone = false, className = '', buttonClassName }) => {
  const t = useT();
  const options = [
    ...(allowNone ? [{ key: '', label: t('prompts.none') }] : []),
    ...prompts.map((p) => ({ key: p.id, label: `${p.label}${p.isDefault ? ` (${t('prompts.default')})` : ''}` })),
  ];
  return (
    <SearchableSelect
      id={id}
      value={value || ''}
      onChange={onChange}
      options={options}
      disabled={disabled}
      placeholder={t('prompts.none')}
      searchPlaceholder={t('prompts.search')}
      ariaLabel={t('prompts.label')}
      className={className}
      buttonClassName={buttonClassName || 'w-full flex items-center justify-between gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-left text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50'}
    />
  );
};

// ─── Manager modal ───────────────────────────────────────────────────────────

const EMPTY_FORM = { id: null, label: '', text: '', isDefault: false };

// alwaysDefault: Program tabs always have exactly one default (the built-in
// prompt to begin with) — the default can be moved to another prompt, not removed.
export const PromptManager = ({ scope, title, prompts, limits, onChanged, onClose, initialText = '', alwaysDefault = false }) => {
  const t = useT();
  // Start straight on a new-prompt form when there's nothing saved yet, or when
  // the page handed over text to save ("Save as prompt").
  const [form, setForm] = useState(
    !prompts.length || initialText ? { ...EMPTY_FORM, text: initialText.slice(0, limits.textMax) } : null
  );
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const full = prompts.length >= limits.perScope;
  // Long lists (up to 200 on Media AI) can be searched by name or text.
  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? prompts.filter((p) => p.label.toLowerCase().includes(needle) || (p.text || '').toLowerCase().includes(needle))
    : prompts;

  const editingBuiltIn = !!(form?.id && prompts.find((p) => p.id === form.id)?.isBuiltIn);

  const save = async () => {
    // The built-in prompt may be saved empty — that's how a user switches it off.
    if (!form.label.trim() || (!form.text.trim() && !editingBuiltIn)) {
      toast.error(t('prompts.needLabelAndText'));
      return;
    }
    setSaving(true);
    try {
      const body = { label: form.label, text: form.text, isDefault: form.isDefault };
      if (form.id) await customPromptService.update(form.id, body);
      else await customPromptService.create({ scope, ...body });
      toast.success(t('prompts.saved'));
      setForm(null);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || t('prompts.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t('prompts.deleteConfirm', { label: p.label }))) return;
    try {
      await customPromptService.remove(p.id);
      if (form?.id === p.id) setForm(null);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || t('prompts.saveFailed'));
    }
  };

  const toggleDefault = async (p) => {
    if (alwaysDefault && p.isDefault) return; // pick another prompt to move the default
    try {
      await customPromptService.update(p.id, { isDefault: !p.isDefault });
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || t('prompts.saveFailed'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl max-h-[92vh] flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('prompts.count', { count: prompts.length, max: limits.perScope })}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label={t('prompts.close')}>
            <FiX size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {prompts.length > 8 && (
            <div className="relative">
              <FiSearch size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('prompts.search')}
                aria-label={t('prompts.search')}
                className="input w-full text-sm pl-8"
              />
            </div>
          )}
          {needle && !shown.length && <p className="text-sm text-gray-400 text-center py-2">{t('prompts.noMatch')}</p>}
          {shown.map((p) => (
            <div
              key={p.id}
              className={`rounded-xl border px-3.5 py-3 ${form?.id === p.id ? 'border-primary-300 bg-primary-50/40' : 'border-gray-200'}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 break-words">
                    {p.label}
                    {p.isBuiltIn && (
                      <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        {t('prompts.standard')}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 whitespace-pre-wrap break-words">
                    {p.text || <span className="italic text-gray-400">{t('prompts.emptyText')}</span>}
                  </p>
                </div>
                <button
                  onClick={() => toggleDefault(p)}
                  disabled={alwaysDefault && p.isDefault}
                  title={p.isDefault ? (alwaysDefault ? t('prompts.isDefault') : t('prompts.unsetDefault')) : t('prompts.setDefault')}
                  className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium disabled:cursor-default ${
                    p.isDefault ? 'text-amber-600 bg-amber-50' : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                  }`}
                >
                  <FiStar size={12} className={p.isDefault ? 'fill-current' : ''} />
                  <span className="hidden sm:inline">{p.isDefault ? t('prompts.default') : t('prompts.makeDefault')}</span>
                </button>
                <button
                  onClick={() => setForm({ id: p.id, label: p.label, text: p.text, isDefault: p.isDefault })}
                  title={t('prompts.edit')}
                  className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                >
                  <FiEdit2 size={13} />
                </button>
                {p.isBuiltIn ? (
                  <span className="flex-shrink-0 w-[25px]" title={t('prompts.cannotDelete')} />
                ) : (
                  <button
                    onClick={() => remove(p)}
                    title={t('prompts.delete')}
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-danger-600 hover:bg-danger-50"
                  >
                    <FiTrash2 size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {form ? (
            <div className="rounded-xl border border-gray-200 p-3.5 space-y-3 bg-gray-50/60">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {form.id ? t('prompts.editTitle') : t('prompts.newTitle')}
              </p>
              <div>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  maxLength={limits.labelMax}
                  placeholder={t('prompts.labelPlaceholder')}
                  className="input w-full text-sm"
                  autoFocus
                />
                <p className="text-right text-[11px] text-gray-400 mt-1">{form.label.length} / {limits.labelMax}</p>
              </div>
              <div>
                <textarea
                  value={form.text}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  maxLength={limits.textMax}
                  rows={8}
                  placeholder={t('prompts.textPlaceholder')}
                  className="input w-full text-sm leading-relaxed resize-y"
                />
                <p className="text-right text-[11px] text-gray-400 mt-1">{form.text.length} / {limits.textMax}</p>
              </div>
              {editingBuiltIn && (
                <p className="text-xs text-gray-500">{t('prompts.builtInHint')}</p>
              )}
              {/* On Program tabs the current default stays default until another prompt is chosen */}
              {!(alwaysDefault && form.id && prompts.find((p) => p.id === form.id)?.isDefault) && (
                <label className="flex items-center gap-2 text-sm text-gray-700 select-none cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                    className="w-4 h-4 rounded accent-primary-500"
                  />
                  {t('prompts.useByDefault')}
                </label>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setForm(null)} className="btn btn-ghost text-sm">{t('prompts.cancel')}</button>
                <button onClick={save} disabled={saving} className="btn btn-primary text-sm">
                  {saving ? t('prompts.saving') : t('prompts.save')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setForm({ ...EMPTY_FORM })}
              disabled={full}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 py-3 text-sm font-medium text-primary-600 hover:bg-primary-50 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
            >
              <FiPlus size={14} />
              {full ? t('prompts.full', { max: limits.perScope }) : t('prompts.add')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
